import { QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { getLargeRandomInt } from "../../../utils/getLargeRandomInt";
import { type User, useUser } from "../../users";
import type { CrudType } from "../../utils/useCrud/CrudType";

export const getUsePost =
  <
    TRequest,
    T extends CrudType,
    OptimisticUpdate = keyof (TRequest & CrudType) extends keyof T
      ? ((item: TRequest & CrudType, user: User) => T) | null
      : (item: TRequest & CrudType, user: User) => T
  >(
    queryKey: (item: TRequest) => QueryKey,
    sendPostItem: (item: TRequest) => Promise<T>,
    optimisticUpdate: OptimisticUpdate
  ) =>
  () => {
    const user = useUser();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (item: TRequest) => sendPostItem(item),
      onMutate: async (item) => {
        await queryClient.cancelQueries(queryKey(item));

        const tempId = getLargeRandomInt();
        const itemWithId: TRequest & CrudType = { ...item, id: tempId };
        const tempItem: T =
          typeof optimisticUpdate === "function"
            ? optimisticUpdate(itemWithId, user)
            : itemWithId;

        queryClient.setQueryData(queryKey(item), (prevItems: T[] = []) => [
          ...prevItems,
          tempItem,
        ]);
        return { tempId };
      },
      onSuccess: (response, _request, context) => {
        queryClient.setQueryData(queryKey(_request), (prevItems: T[] = []) =>
          prevItems.map((prevItem) =>
            prevItem.id === context?.tempId ? response : prevItem
          )
        );
      },
    });
  };
